import { useIsMobile } from '../../hooks/useMobile';
import AutoTextarea from '../AutoTextarea';
import React, { useState, useMemo } from 'react';
import { TasksTabProps, Task } from './types';
import { ENERGY_TO_LABEL, ENERGY_COLORS, ENERGY_DESCRIPTIONS } from './energyMap';
import { useOptimisticTasks } from './useOptimisticTasks';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  pointerWithin,
  rectIntersection,
  useDroppable,
  MeasuringStrategy,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Task Item Component
const SortableTaskItem: React.FC<{
  task: Task;
  children: React.ReactNode;
}> = ({ task, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useSortable({ 
    id: task.id,
    animateLayoutChanges: () => false, // Полностью отключаем анимацию
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: 'none', // Полностью отключаем transition
    opacity: isDragging ? 0.5 : 1,
    touchAction: 'pan-y',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="sortable-card"
    >
      <button type="button" className="drag-handle" {...attributes} {...listeners} aria-label="Перетащить карточку">⠿</button>
      {children}
    </div>
  );
};

// Droppable Column Component with useDroppable
const DroppableColumn: React.FC<{
  id: string;
  tasks: Task[];
  children: React.ReactNode;
}> = ({ id, tasks, children }) => {
  const isMobile = useIsMobile();
  const { setNodeRef, isOver } = useDroppable({
    id: id,
  });
  
  const taskIds = tasks.map(t => t.id);
  
  return (
    <div 
      ref={setNodeRef}
      style={{ 
        minHeight: '100%',
        height: '100%',
        background: isOver ? '#dbeafe' : 'transparent',
        borderRadius: '8px',
        transition: 'none',
        border: isOver ? '2px dashed #3b82f6' : '2px dashed transparent',
        padding: '4px'
      }}
    >
      <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
        {children}
        {/* Пустое место для бросания в конец */}
        {tasks.length === 0 && (
          <div style={{ 
            padding: '40px 20px', 
            textAlign: 'center', 
            color: '#9ca3af',
            fontSize: '13px'
          }}>
            {isMobile ? 'В этой колонке пока нет дел' : 'Перетащите задачи сюда'}
          </div>
        )}
      </SortableContext>
    </div>
  );
};

const TasksTab: React.FC<TasksTabProps> = ({
  tasks,
  goals,
  categories,
  showTaskForm,
  setShowTaskForm,
  selectedGoal,
  setSelectedGoal,
  filterEnergy,
  setFilterEnergy,
  filterSprint,
  setFilterSprint,
  filterStatus,
  setFilterStatus,
  filterCategory,
  setFilterCategory,
  taskForm,
  setTaskForm,
  editingTaskId,
  setEditingTaskId,
  handleCreateTask,
  handleUpdateTask,
  handleEditTask,
  handleCancelEdit,
  handleDeleteTask,
  handleUpdateTaskStatus,
  handleReorderTasks,
  loadData
}) => {
  // Drag and drop state
  const isMobile = useIsMobile();
  const [mobileColumn, setMobileColumn] = useState('today');
  const [movingTaskId, setMovingTaskId] = useState<number | null>(null);
  const [moveNotice, setMoveNotice] = useState<{ text: string; error: boolean } | null>(null);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  
  const { displayTasks, applyTaskPatches } = useOptimisticTasks(tasks);

  // Определяем мобильное устройство
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const sensors = useSensors(
    // PointerSensor для мыши (только на десктопе)
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    // TouchSensor для тача - с увеличенной задержкой для надёжности
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,      // 250ms удержание для начала драга
        tolerance: 8,    // допустимое движение пальца при удержании
      },
    }),
    useSensor(KeyboardSensor)
  );
  
  // Модалка для выбора даты при архивации
  const [archivingTaskId, setArchivingTaskId] = useState<number | null>(null);
  const [archiveDate, setArchiveDate] = useState<string>('');

  // Оптимистичное обновление статуса задачи — меняет UI мгновенно
  const applyStatusUpdate = (taskId: number, newStatus: Task['status']) => {
    return applyTaskPatches([{ id: taskId, status: newStatus }]);
  };

  const saveStatus = async (taskId: number, status: Task['status'], completedAt?: string) => {
    const finish = applyStatusUpdate(taskId, status);
    try {
      await handleUpdateTaskStatus(taskId, status, completedAt);
      finish(true);
    } catch {
      finish(false);
      setMoveNotice({ text: 'Не удалось сохранить дело. Попробуйте ещё раз.', error: true });
    }
  };
  
  // Раскрытые карточки задач
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  // Карточки с переполнением (нужна кнопка раскрытия)
  const [overflowingCards, setOverflowingCards] = useState<Set<number>>(new Set());
  
  const toggleCardExpand = (taskId: number) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedCards(newExpanded);
  };

  // Проверка переполнения текста - запускается ОДИН РАЗ после рендера
  React.useEffect(() => {
    // Небольшая задержка чтобы DOM успел отрендериться
    const timer = setTimeout(() => {
      const newOverflowing = new Set<number>();
      
      displayTasks.forEach(task => {
        if (!expandedCards.has(task.id)) {
          const titleEl = document.querySelector(`[data-task-title="${task.id}"]`) as HTMLDivElement;
          const descEl = document.querySelector(`[data-task-desc="${task.id}"]`) as HTMLDivElement;
          
          let hasOverflow = false;
          if (titleEl && titleEl.scrollHeight > titleEl.clientHeight) {
            hasOverflow = true;
          }
          if (descEl && descEl.scrollHeight > descEl.clientHeight) {
            hasOverflow = true;
          }
          
          if (hasOverflow) {
            newOverflowing.add(task.id);
          }
        }
      });
      
      setOverflowingCards(newOverflowing);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [displayTasks, expandedCards]);
  
  const handleArchiveClick = (taskId: number) => {
    setArchivingTaskId(taskId);
    // Дефолт - текущая дата
    setArchiveDate(new Date().toISOString().split('T')[0]);
  };
  
  const confirmArchive = async () => {
    if (archivingTaskId) {
      const completedAt = new Date(archiveDate + 'T12:00:00').toISOString();
      // Мгновенно убираем из UI
      const taskId = archivingTaskId;
      setArchivingTaskId(null);
      await saveStatus(taskId, 'archived', completedAt);
    }
  };
  
  // Фильтрация задач (используем displayTasks для визуального обновления)
  // МЕМОИЗИРОВАНО для предотвращения блокировки event loop
  const filteredTasks = useMemo(() => displayTasks.filter(task => {
    if (selectedGoal === 'none' && task.goalId !== null) return false;
    if (typeof selectedGoal === 'number' && task.goalId !== selectedGoal) return false;
    if (filterEnergy !== 'all' && task.energy !== filterEnergy) return false;
    if (filterSprint !== 'all' && task.sprint !== filterSprint) return false;
    if (filterCategory !== 'all' && task.categoryId !== filterCategory) return false;
    // Фильтр по статусу (активные/архив)
    if (filterStatus === 'active' && task.status === 'archived') return false;
    if (filterStatus === 'archived' && task.status !== 'archived') return false;
    return true;
  }), [displayTasks, selectedGoal, filterEnergy, filterSprint, filterCategory, filterStatus]);

  // Группировка по статусам (с сортировкой по order)
  // МЕМОИЗИРОВАНО для предотвращения блокировки event loop
  const tasksByStatus = useMemo(() => filterStatus === 'archived' 
    ? {
        archived: filteredTasks.filter(t => t.status === 'archived').sort((a, b) => {
          const dateA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
          const dateB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
          return dateB - dateA; // новые сверху
        })
      }
    : {
        planned: filteredTasks.filter(t => t.status === 'planned').sort((a, b) => (a.order || 0) - (b.order || 0)),
        in_progress_mine: filteredTasks.filter(t => t.status === 'in_progress_mine').sort((a, b) => (a.order || 0) - (b.order || 0)),
        in_progress_waiting: filteredTasks.filter(t => t.status === 'in_progress_waiting').sort((a, b) => (a.order || 0) - (b.order || 0)),
        today: filteredTasks.filter(t => t.status === 'today').sort((a, b) => (a.order || 0) - (b.order || 0)),
        done: filteredTasks.filter(t => t.status === 'done').sort((a, b) => (a.order || 0) - (b.order || 0))
      }, [filteredTasks, filterStatus]);

  const statusLabels = {
    planned: '📋 Запланировано',
    in_progress_mine: '⚙️ В процессе (мое)',
    in_progress_waiting: '⏳ В процессе (жду)',
    today: '🎯 Сегодня',
    done: '✅ Сделано',
    archived: '📦 Архив'
  } as const;

  const moveTask = async (task: Task, status: Task['status']) => {
    if (movingTaskId !== null || status === task.status) return;
    setMovingTaskId(task.id);
    setMoveNotice(null);
    try {
      await handleUpdateTaskStatus(task.id, status);
    } catch {
      setMoveNotice({ text: 'Не удалось переместить дело. Попробуйте ещё раз.', error: true });
      setMovingTaskId(null);
      return;
    }
    applyStatusUpdate(task.id, status)(true);
    setMoveNotice({ text: `«${task.name}» перемещено в «${statusLabels[status]}».`, error: false });
    setMovingTaskId(null);
  };

  // МЕМОИЗИРОВАНО для предотвращения блокировки event loop
  const sprints = useMemo(() => Array.from(new Set(displayTasks.map(t => t.sprint).filter(Boolean))), [displayTasks]);

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const task = displayTasks.find(t => t.id === active.id);
    setActiveTask(task || null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    // Просто ничего не делаем - без визуального обновления во время драга
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);
    
    if (!over) return;

    const activeId = active.id as number;
    const overId = over.id;

    const draggedTask = displayTasks.find((t: Task) => t.id === activeId);
    if (!draggedTask) return;

    // Определяем целевой статус
    let targetStatus: Task['status'] | null = null;

    // Если overId - строка статуса (упали на колонку напрямую)
    if (typeof overId === 'string' && overId in tasksByStatus) {
      targetStatus = overId as Task['status'];
    } else if (typeof overId === 'number') {
      // Упали на задачу - берем ее статус
      const foundTask = displayTasks.find((t: Task) => t.id === overId);
      if (foundTask) {
        targetStatus = foundTask.status;
      }
    }

    if (!targetStatus) return;

    const sourceStatus = draggedTask.status;

    // Если перетащили в архив - открываем диалог архивации
    if (targetStatus === 'archived') {
      setArchivingTaskId(activeId);
      setArchiveDate(new Date().toISOString().split('T')[0]);
      return;
    }

    // Если перетащили в другую колонку
    if (targetStatus !== sourceStatus) {
      // МГНОВЕННО обновляем UI - меняем статус задачи локально
      const updatedTask = { ...draggedTask, status: targetStatus };
      const otherTasks = displayTasks.filter((t: Task) => t.id !== activeId);
      
      // Получаем задачи целевой колонки
      const targetColumnTasks = otherTasks.filter((t: Task) => t.status === targetStatus)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // Вставляем задачу в нужную позицию
      if (typeof overId === 'number') {
        const overIndex = targetColumnTasks.findIndex((t: Task) => t.id === overId);
        if (overIndex !== -1) {
          targetColumnTasks.splice(overIndex, 0, updatedTask);
        } else {
          targetColumnTasks.push(updatedTask);
        }
      } else {
        targetColumnTasks.push(updatedTask);
      }
      
      // Обновляем order для всех задач в целевой колонке
      const reorderedTarget = targetColumnTasks.map((t, index) => ({ ...t, order: index + 1 }));
      
      const finish = applyTaskPatches(reorderedTarget.map(t => ({
        id: t.id, order: t.order, ...(t.id === activeId ? { status: targetStatus! } : {})
      })));
      
      // Теперь сохраняем на сервере в фоне (БЕЗ перезагрузки UI)
      try {
        const newOrder = reorderedTarget.map((t: Task) => t.id);
        await handleReorderTasks(newOrder, { id: activeId, status: targetStatus });
        finish(true);
      } catch (error) {
        finish(false);
        console.error('Ошибка при перетаскивании:', error);
        setMoveNotice({ text: 'Не удалось переместить дело. Попробуйте ещё раз.', error: true });
        // При ошибке откатываем изменения
        await loadData();
      }
      
    } else if (activeId !== overId) {
      // Перетащили внутри одной колонки
      const statusTasks = displayTasks.filter((t: Task) => t.status === targetStatus)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const oldIndex = statusTasks.findIndex((t: Task) => t.id === activeId);
      const newIndex = statusTasks.findIndex((t: Task) => t.id === overId);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        // МГНОВЕННО переставляем в UI
        const reordered = [...statusTasks];
        const [removed] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, removed);
        
        // Обновляем order
        const reorderedWithOrder = reordered.map((t, index) => ({ ...t, order: index + 1 }));
        
        const finish = applyTaskPatches(reorderedWithOrder.map(t => ({ id: t.id, order: t.order })));
        
        // Сохраняем на сервере в фоне (БЕЗ перезагрузки UI)
        try {
          const newOrder = reorderedWithOrder.map((t: Task) => t.id);
          await handleReorderTasks(newOrder);
          finish(true);
        } catch (error) {
          finish(false);
          console.error('Ошибка при изменении порядка:', error);
          setMoveNotice({ text: 'Не удалось сохранить порядок дел. Попробуйте ещё раз.', error: true });
          // При ошибке откатываем изменения
          await loadData();
        }
      }
    }
  };

  return (
    <div>
      {/* Форма создания/редактирования задачи */}
      {showTaskForm && (
        <form onSubmit={editingTaskId ? handleUpdateTask : handleCreateTask} style={{
          background: '#f9fafb',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h3 style={{ marginTop: 0 }}>{editingTaskId ? 'Редактировать дело' : 'Новое дело'}</h3>
          <input
            type="text"
            placeholder="Название дела"
            value={taskForm.name}
            onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
            required
            style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px' }}
          />
          <AutoTextarea
            placeholder="Описание (опционально)"
            value={taskForm.description}
            onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
            rows={2}
            style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px', resize: 'vertical' }}
          />
          <select
            value={taskForm.goalId || ''}
            onChange={(e) => setTaskForm({ ...taskForm, goalId: e.target.value ? parseInt(e.target.value) : null })}
            style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px' }}
          >
            <option value="">Без цели</option>
            {goals.filter(g => g.status === 'active').map(goal => (
              <option key={goal.id} value={goal.id}>{goal.name}</option>
            ))}
          </select>
          
          <select
            value={taskForm.categoryId || ''}
            onChange={(e) => setTaskForm({ ...taskForm, categoryId: e.target.value ? Number(e.target.value) : null })}
            style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px' }}
          >
            <option value="">Без категории</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <select
              value={taskForm.energy}
              onChange={(e) => setTaskForm({ ...taskForm, energy: e.target.value as '1' | '2' | '3' })}
              style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            >
              <option value="1">А (сильно затратно, &gt;60 мин)</option>
              <option value="2">Б (средне, 5-60 мин)</option>
              <option value="3">В (легко, &lt;5 мин)</option>
            </select>
            <input
              type="text"
              placeholder="Спринт (например, 2025-W44)"
              value={taskForm.sprint}
              onChange={(e) => setTaskForm({ ...taskForm, sprint: e.target.value })}
              style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>
          <input
            type="date"
            value={taskForm.dueDate}
            onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
            style={{ width: '100%', padding: '8px', marginBottom: '10px', border: '1px solid #d1d5db', borderRadius: '4px' }}
          />
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" style={{
              flex: 1,
              padding: '10px',
              background: editingTaskId ? '#2563eb' : '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}>
              {editingTaskId ? 'Сохранить' : 'Создать дело'}
            </button>
            {editingTaskId && (
              <button type="button" onClick={handleCancelEdit} style={{
                padding: '10px 20px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}>
                Отмена
              </button>
            )}
          </div>
        </form>
      )}

      {/* Фильтры */}
      <div className="task-filters" style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        display: 'flex',
        gap: '15px',
        flexWrap: 'wrap',
        alignItems: 'center',
        boxShadow: '0 2px 15px rgba(0,0,0,0.06)'
      }}>
        <div>
          <label style={{ fontSize: '12px', color: '#6b7280', marginRight: '5px' }}>Цель:</label>
          <select
            value={selectedGoal === null ? '' : selectedGoal}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedGoal(val === '' ? null : val === 'none' ? 'none' : parseInt(val));
            }}
            style={{ padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
          >
            <option value="">Все цели</option>
            <option value="none">Только дела без цели</option>
            {goals.filter(g => g.status === 'active').map(goal => (
              <option key={goal.id} value={goal.id}>{goal.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ fontSize: '12px', color: '#6b7280', marginRight: '5px' }}>Энергозатратность:</label>
          <select
            value={filterEnergy}
            onChange={(e) => setFilterEnergy(e.target.value)}
            style={{ padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
          >
            <option value="all">Все</option>
            <option value="1">А (&gt;60 мин)</option>
            <option value="2">Б (5-60 мин)</option>
            <option value="3">В (&lt;5 мин)</option>
          </select>
        </div>

        <div>
          <label style={{ fontSize: '12px', color: '#6b7280', marginRight: '5px' }}>Спринт:</label>
          <select
            value={filterSprint}
            onChange={(e) => setFilterSprint(e.target.value)}
            style={{ padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
          >
            <option value="all">Все</option>
            {sprints.map(sprint => (
              <option key={sprint!} value={sprint!}>{sprint}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ fontSize: '12px', color: '#6b7280', marginRight: '5px' }}>Категория:</label>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
            style={{ padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
          >
            <option value="all">Все</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ fontSize: '12px', color: '#6b7280', marginRight: '5px' }}>Статус:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'archived')}
            style={{ padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', fontWeight: filterStatus === 'archived' ? 'bold' : 'normal' }}
          >
            <option value="active">📌 Активные</option>
            <option value="archived">📦 Архив</option>
          </select>
        </div>
      </div>

      {isMobile && filterStatus !== 'archived' && <label className="task-column-picker">Колонка дел<select value={mobileColumn} onChange={e => setMobileColumn(e.target.value)}>{(Object.keys(statusLabels) as Array<keyof typeof statusLabels>).filter(key => key !== 'archived').map(key => <option key={key} value={key}>{statusLabels[key]} · {tasksByStatus[key]?.length || 0}</option>)}</select></label>}
      {moveNotice && <p role={moveNotice.error ? 'alert' : 'status'} style={{ padding: '12px', borderRadius: '8px', background: moveNotice.error ? '#fef2f2' : '#ecfdf5', color: moveNotice.error ? '#991b1b' : '#065f46', overflowWrap: 'anywhere' }}>{moveNotice.text}</p>}
      {/* Канбан-доска с drag and drop между колонками */}
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always
          }
        }}
      >
        <div className={`task-board${filterStatus === 'archived' ? ' task-board-archive' : ''}`} style={{
          display: 'grid',
          gridTemplateColumns: filterStatus === 'archived' ? '1fr' : 'repeat(5, minmax(0, 1fr))',
          gap: '15px'
        }}>
          {(Object.keys(tasksByStatus) as Array<keyof typeof tasksByStatus>).filter(status => !isMobile || filterStatus === 'archived' || status === mobileColumn).map(status => (
          <div key={status} style={{
            background: '#f9fafb',
            borderRadius: '8px',
            padding: '12px',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#1f2937',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              {statusLabels[status]}
              <span style={{
                background: '#e5e7eb',
                borderRadius: '12px',
                padding: '2px 8px',
                fontSize: '11px',
                color: '#6b7280'
              }}>
                {tasksByStatus[status]?.length || 0}
              </span>
            </h3>

            {/* Droppable column with tasks */}
            <DroppableColumn id={status} tasks={tasksByStatus[status] || []}>
              {(tasksByStatus[status] || []).map((task: Task) => {
                  const isExpanded = expandedCards.has(task.id);
                  const needsExpandButton = overflowingCards.has(task.id);
                  const isEditing = editingTaskId === task.id;
                  
                  let titleRef: HTMLDivElement | null = null;
                  let descRef: HTMLDivElement | null = null;
                  
                  const taskCard = (
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                      borderLeft: task.category ? `4px solid ${task.category.color}` : '4px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '14px',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                      maxHeight: isMobile || isExpanded || isEditing ? 'none' : '280px',
                      overflow: 'hidden',
                      position: 'relative',
                      transition: 'max-height 0.3s ease'
                    }}
                  >
                    {/* Весь контент карточки задачи */}
                      {editingTaskId === task.id ? (
                        <form onSubmit={handleUpdateTask} style={{ width: '100%' }}>
                          <h4 style={{ marginTop: 0, marginBottom: '10px', fontSize: '14px' }}>Редактировать дело</h4>
                          
                          <input
                            type="text"
                            placeholder="Название дела"
                            value={taskForm.name}
                            onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                            required
                            style={{ width: '100%', padding: '6px', marginBottom: '8px', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box', fontSize: '13px' }}
                          />
                          
                          <AutoTextarea
                            placeholder="Описание (опционально)"
                            value={taskForm.description}
                            onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                            rows={2}
                            style={{ width: '100%', padding: '6px', marginBottom: '8px', border: '1px solid #d1d5db', borderRadius: '4px', resize: 'vertical', boxSizing: 'border-box', fontSize: '13px' }}
                          />
                          
                          <select
                            value={taskForm.categoryId || ''}
                            onChange={(e) => setTaskForm({ ...taskForm, categoryId: e.target.value ? Number(e.target.value) : null })}
                            style={{ width: '100%', padding: '6px', marginBottom: '8px', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box', fontSize: '13px' }}
                          >
                            <option value="">Без категории</option>
                            {categories.map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                            ))}
                          </select>
                          
                          <select
                            value={taskForm.goalId || ''}
                            onChange={(e) => setTaskForm({ ...taskForm, goalId: e.target.value ? Number(e.target.value) : null })}
                            style={{ width: '100%', padding: '6px', marginBottom: '8px', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box', fontSize: '13px' }}
                          >
                            <option value="">Без цели</option>
                            {goals.filter(g => g.status === 'active').map(g => (
                              <option key={g.id} value={g.id}>🎯 {g.name}</option>
                            ))}
                          </select>
                          
                          <select
                            value={taskForm.energy}
                            onChange={(e) => setTaskForm({ ...taskForm, energy: e.target.value as '1' | '2' | '3' })}
                            required
                            style={{ width: '100%', padding: '6px', marginBottom: '8px', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box', fontSize: '13px' }}
                          >
                            <option value="1">А - Высокий приоритет</option>
                            <option value="2">Б - Средний приоритет</option>
                            <option value="3">В - Низкий приоритет</option>
                          </select>
                          
                          <input
                            type="text"
                            placeholder="Спринт (опционально)"
                            value={taskForm.sprint}
                            onChange={(e) => setTaskForm({ ...taskForm, sprint: e.target.value })}
                            style={{ width: '100%', padding: '6px', marginBottom: '8px', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box', fontSize: '13px' }}
                          />
                          
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                            <input
                              type="date"
                              value={taskForm.dueDate || ''}
                              onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                              style={{ flex: 1, padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box', fontSize: '13px' }}
                              placeholder="Дата"
                            />
                            <input
                              type="time"
                              value={taskForm.dueTime || ''}
                              onChange={(e) => setTaskForm({ ...taskForm, dueTime: e.target.value })}
                              style={{ flex: 1, padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box', fontSize: '13px' }}
                              placeholder="Время"
                            />
                          </div>
                          
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="submit" style={{ flex: 1, padding: '8px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                              Сохранить
                            </button>
                            <button type="button" onClick={handleCancelEdit} style={{ padding: '8px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                              Отмена
                            </button>
                          </div>
                        </form>
                      ) : (
                        <>
                          {/* Название задачи */}
                          <div 
                            data-task-title={task.id}
                            ref={(el) => {
                              titleRef = el;
                            }}
                            style={{ 
                              fontWeight: '500', 
                              fontSize: '13px', 
                              color: '#1f2937',
                              marginBottom: '6px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              wordBreak: 'break-word',
                              display: '-webkit-box',
                              WebkitLineClamp: isExpanded ? 'unset' : 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {task.name}
                          </div>

                          {task.description && (
                            <div 
                              data-task-desc={task.id}
                              ref={(el) => {
                                descRef = el;
                              }}
                              style={{ 
                                fontSize: '11px', 
                                color: '#6b7280', 
                                marginBottom: '6px', 
                                fontStyle: 'italic', 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                wordBreak: 'break-word',
                                display: isExpanded ? 'block' : '-webkit-box',
                                WebkitLineClamp: isExpanded ? 'unset' : 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {task.description}
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
                            <span style={{ 
                              padding: '2px 6px', 
                              borderRadius: '3px', 
                              fontSize: '10px', 
                              background: ENERGY_COLORS[task.energy], 
                              color: 'white', 
                              fontWeight: 'bold',
                              flexShrink: 0
                            }}>
                              {ENERGY_TO_LABEL[task.energy]}
                            </span>
                            {task.goal && (
                              <span style={{ 
                                padding: '2px 6px', 
                                borderRadius: '3px', 
                                fontSize: '10px', 
                                background: '#dbeafe', 
                                color: '#1e40af',
                                maxWidth: '120px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                🎯 {task.goal.name}
                              </span>
                            )}
                            {task.dueDate && (() => {
                              const today = new Date().toISOString().split('T')[0];
                              const isToday = task.dueDate === today;
                              
                              return (
                                <span style={{ padding: '2px 6px', borderRadius: '3px', fontSize: '10px', background: isToday ? '#fecaca' : '#fef3c7', color: isToday ? '#7f1d1d' : '#92400e', fontWeight: isToday ? 'bold' : 'normal' }}>
                                  {isToday ? (
                                    task.dueTime ? `⏰ ${task.dueTime.substring(0, 5)}` : '📅 Сегодня'
                                  ) : (
                                    `📅 ${new Date(task.dueDate).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}${task.dueTime ? ` ⏰ ${task.dueTime.substring(0, 5)}` : ''}`
                                  )}
                                </span>
                              );
                            })()}
                          </div>

                          {isMobile && task.status !== 'archived' && (
                            <label style={{ display: 'grid', gap: '6px', marginTop: '10px', fontSize: '13px', color: '#374151' }}>
                              {movingTaskId === task.id ? 'Перемещаем…' : 'Переместить в…'}
                              <select
                                aria-label={`Переместить дело «${task.name}» в колонку`}
                                value=""
                                disabled={movingTaskId !== null}
                                onChange={e => { void moveTask(task, e.target.value as Task['status']); }}
                                style={{ width: '100%', minWidth: 0, minHeight: '44px', padding: '8px', border: '1px solid #c4b5fd', borderRadius: '8px', background: '#fff', color: '#5b21b6' }}
                              >
                                <option value="" disabled>Выберите колонку</option>
                                {(Object.keys(statusLabels) as Array<Task['status']>).filter(key => key !== 'archived' && key !== task.status).map(key => <option key={key} value={key}>{statusLabels[key]}</option>)}
                              </select>
                            </label>
                          )}

                          {/* Кнопки управления задачей */}
                          <div style={{ display: 'flex', gap: '6px', marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #f3f4f6' }}>
                            {task.status === 'archived' ? (
                              <>
                                {task.completedAt && (
                                  <div style={{ width: '100%', fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>
                                    📦 {new Date(task.completedAt).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                  </div>
                                )}
                              <button 
                                onClick={() => saveStatus(task.id, 'planned')}
                                style={{ 
                                  flex: 1,
                                  padding: '6px 10px',
                                  background: '#10b981', 
                                  color: 'white',
                                  border: 'none', 
                                  borderRadius: '4px',
                                  cursor: 'pointer', 
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}
                                title="Восстановить"
                              >
                                ↩️ Восстановить
                              </button>
                              </>
                            ) : task.status === 'done' ? (
                              <>
                                <button 
                                  onClick={() => saveStatus(task.id, 'archived')}
                                  style={{ 
                                    flex: 1,
                                    padding: '6px 10px',
                                    background: '#f59e0b', 
                                    color: 'white',
                                    border: 'none', 
                                    borderRadius: '4px',
                                    cursor: 'pointer', 
                                    fontSize: '12px',
                                    fontWeight: '600'
                                  }}
                                  title="Переместить в архив"
                                >
                                  📦 В архив
                                </button>
                                <button 
                                  onClick={() => handleEditTask(task)} 
                                  style={{ 
                                    padding: '6px 12px',
                                    background: '#e0e7ff', 
                                    color: '#3730a3',
                                    border: 'none', 
                                    borderRadius: '4px',
                                    cursor: 'pointer', 
                                    fontSize: '12px',
                                    fontWeight: '600'
                                  }}
                                  title="Редактировать"
                                >
                                  ✏️
                                </button>
                              </>
                            ) : (
                              <button 
                                onClick={() => handleEditTask(task)} 
                                style={{ 
                                  flex: 1,
                                  padding: '6px 10px',
                                  background: '#e0e7ff', 
                                  color: '#3730a3',
                                  border: 'none', 
                                  borderRadius: '4px',
                                  cursor: 'pointer', 
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}
                                title="Редактировать"
                              >
                                ✏️ Редактировать
                              </button>
                            )}
                            <button 
                              onClick={() => handleDeleteTask(task.id)} 
                              style={{ 
                                padding: '6px 12px',
                                background: '#fee2e2', 
                                color: '#991b1b',
                                border: 'none', 
                                borderRadius: '4px',
                                cursor: 'pointer', 
                                fontSize: '12px',
                                fontWeight: '600'
                              }}
                              title="Удалить"
                            >
                              🗑️
                            </button>
                          </div>

                          {/* Кнопка раскрытия/сворачивания - только если контент не помещается */}
                          {needsExpandButton && !isExpanded && (
                            <div 
                              style={{
                                textAlign: 'center',
                                marginTop: '12px',
                                paddingTop: '8px',
                                borderTop: '1px dashed #e5e7eb',
                                cursor: 'pointer'
                              }}
                              onClick={() => toggleCardExpand(task.id)}
                            >
                              <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>⬇️ Показать больше</span>
                            </div>
                          )}
                          {needsExpandButton && isExpanded && (
                            <div 
                              style={{
                                textAlign: 'center',
                                marginTop: '12px',
                                paddingTop: '8px',
                                borderTop: '1px dashed #e5e7eb',
                                cursor: 'pointer'
                              }}
                              onClick={() => toggleCardExpand(task.id)}
                            >
                              <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>⬆️ Скрыть</span>
                            </div>
                          )}
                        </>
                      )}
                  </div>
                  );
                  
                  // Оборачиваем в SortableTaskItem только если НЕ редактируем
                  return isEditing ? taskCard : (
                    <SortableTaskItem key={task.id} task={task}>
                      {taskCard}
                    </SortableTaskItem>
                  );
              })}
            </DroppableColumn>
          </div>
        ))}
      </div>
      
      {/* Overlay для перетаскиваемого элемента */}
      <DragOverlay>
        {activeTask ? (
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
            borderLeft: activeTask.category ? `4px solid ${activeTask.category.color}` : '4px solid #e5e7eb',
            borderRadius: '8px',
            padding: '14px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            cursor: 'grabbing',
            opacity: 0.9,
            minWidth: '200px'
          }}>
            <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
              {activeTask.name}
            </div>
            {activeTask.description && (
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {activeTask.description.substring(0, 50)}...
              </div>
            )}
          </div>
        ) : null}
      </DragOverlay>
      </DndContext>

      {/* Модалка для выбора даты архивации */}
      {archivingTaskId && (
        <div className="mobile-dialog-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setArchivingTaskId(null)}>
          <div style={{
            background: 'white',
            padding: '25px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '18px', fontWeight: 'bold' }}>
              📦 Отправить в архив
            </h3>
            <p style={{ marginBottom: '15px', fontSize: '14px', color: '#6b7280' }}>
              Укажите дату, когда дело было завершено:
            </p>
            <input
              type="date"
              value={archiveDate}
              onChange={(e) => setArchiveDate(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '20px'
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setArchivingTaskId(null)}
                style={{
                  padding: '8px 16px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Отмена
              </button>
              <button
                onClick={confirmArchive}
                style={{
                  padding: '8px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                В архив
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksTab;
